import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventsList } from '@/mocks/events';
import { useSeo } from '@/hooks/useSeo';
import PageBanner from '@/components/base/PageBanner';
import EventCard from '@/components/base/EventCard';
import Reveal from '@/components/base/Reveal';
import { formatDate } from '@/utils/date';

const STATUS_TABS = [
  { value: 'all', label: '全部活动' },
  { value: 'ongoing', label: '进行中' },
  { value: 'upcoming', label: '即将开启' },
  { value: 'ended', label: '已结束' },
];

const DOT_STYLES: Record<string, string> = {
  ongoing: 'bg-accent-500',
  upcoming: 'bg-primary-500',
  ended: 'bg-secondary-400',
};

export default function Events() {
  useSeo({
    title: '原神最新活动与福利专题 – 原神官方游戏站',
    description: '原神最新限时活动与福利专题，汇总活动时间、奖励内容与参与建议，帮助玩家不错过任何一次福利。',
    keywords: '原神活动,原神福利,限时活动,原神活动时间,活动奖励',
    canonicalPath: '/events',
  });

  const [status, setStatus] = useState('all');

  const list = useMemo(
    () => eventsList.filter((item) => (status === 'all' ? true : item.status === status)).sort((a, b) => (a.start < b.start ? 1 : -1)),
    [status],
  );

  return (
    <div>
      <PageBanner
        kicker="EVENTS"
        title="原神最新游戏活动与福利"
        subtitle="当前进行中与即将开启的限时活动一览，包含活动封面、简介、奖励内容与参与建议。"
        crumbs={[{ label: '首页', to: '/' }, { label: '活动' }]}
        image={eventsList[0].cover}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                status === tab.value
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="relative border-l-2 border-background-200 pl-5 md:pl-8">
            <div className="space-y-10">
              {list.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <div className="relative">
                    <span
                      className={`absolute -left-[27px] top-6 h-4 w-4 rounded-full border-4 border-background-100 md:-left-[39px] ${DOT_STYLES[item.status] ?? 'bg-primary-500'}`}
                    />
                    <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground-800">
                        <i className="ri-calendar-schedule-line"></i>
                        {formatDate(item.start)} — {formatDate(item.end)}
                      </span>
                      <span className="text-foreground-400">剩余 {Math.max(0, Math.round((new Date(item.end).getTime() - new Date('2026-10-12').getTime()) / 86400000))} 天</span>
                    </div>
                    <EventCard item={item} />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-calendar-close-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">该分类下暂无活动</p>
            <p className="mt-1.5 text-sm text-foreground-500">切换其他状态查看活动内容。</p>
          </div>
        )}

        <div className="mt-14 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="font-heading text-base font-bold text-foreground-950">
            <a href="#related-topics">相关推荐专题</a>
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: '版本前瞻汇总', to: '/updates' },
              { label: '深渊满星攻略', to: '/search?q=深渊' },
              { label: '新手入门指南', to: '/search?q=新手' },
              { label: '角色养成规划', to: '/characters' },
            ].map((topic) => (
              <Link
                key={topic.to}
                to={topic.to}
                className="group flex cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="text-sm text-foreground-800 transition-colors group-hover:text-primary-700">{topic.label}</span>
                <i className="ri-arrow-right-line text-primary-600"></i>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}